import { Dialog } from '@headlessui/react';
import { forwardRef, JSX, useEffect } from 'react';

import { CloseIcon } from '~components/icons/CloseIcon';
import { Asset } from '~modules/media-library/model/Asset';
import { AssetActions } from '~modules/media-library/model/AssetActions';
import { Button } from '~modules/media-library/view/Button';
import { PanelActions } from '~modules/media-library/view/DetailModal/PanelActions';
import { PanelFigure } from '~modules/media-library/view/DetailModal/PanelFigure';
import { PanelTabs } from '~modules/media-library/view/DetailModal/PanelTabs';

type DetailModalPanelProps = {
  asset: Asset;
  onClose?: () => void;
  AssetActions: AssetActions;
};

export const DetailModalPanel = forwardRef<HTMLDivElement, JSX.IntrinsicElements['div'] & DetailModalPanelProps>(
  function DetailModalPanel({ asset, onClose, className, AssetActions, ...TheirProps }, ref) {
    const hasAsset = Boolean(asset);

    useEffect(() => {
      if (!hasAsset) {
        onClose?.();
      }
    }, [hasAsset, onClose]);

    if (!hasAsset) return null;

    let modalBody = (
      <>
        <div className="ml--detail-modal--panel-body overflow-x-hidden overflow-y-scroll text-left">
          <PanelFigure asset={asset} AssetActions={AssetActions} />
          <PanelActions asset={asset} AssetActions={AssetActions} />
          <div className="ml--detail-modal--panel-tabs p-3">
            <PanelTabs asset={asset} />
          </div>
        </div>
        {Boolean(asset.attributes.legalNotice) && (
          <div className="ml--detail-modal--panel-footer flex justify-between items-center border-b-0 border-l-0 border-r-0 border-t border-solid border-darkgray py-2 px-3">
            <div className="font-sans select-none text-red text-sm">{asset.attributes.legalNotice}</div>
          </div>
        )}
      </>
    );
    // show message for review view, if asset is not in review
    if (window.location.pathname.endsWith('/review') && !asset.attributes.workflowStatus.endsWith('review')) {
      modalBody = <div className="ml--detail-modal--panel-body pl-3 py-2">Asset not in review!</div>;
    }

    return (
      <div
        className={`ml--detail-modal--panel bg-white overflow-hidden rounded-md shadow-xl w-full max-w-md min-h-0 max-h-full flex flex-col relative ${
          className ? className : ''
        }`}
        {...TheirProps}
        ref={ref}
      >
        <header className="ml--detail-modal--panel-header flex pl-3 py-2 pr-8">
          <Dialog.Title
            as="h2"
            className="ml--detail-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0 h-10 clamp-2"
          >
            <span dangerouslySetInnerHTML={{ __html: asset.attributes.title }} />
          </Dialog.Title>
          <Dialog.Description as="div" className="sr-only">
            {asset.attributes.description}
          </Dialog.Description>
        </header>
        <Button variant="icon" className="absolute top-0 right-0 border-none" onClick={onClose}>
          <CloseIcon />
        </Button>
        {modalBody}
      </div>
    );
  },
);
